// src/services/authService.ts

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  type InitiateAuthCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider';
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from '@aws-sdk/client-cognito-identity';
import type { Session, AwsCredentials } from '../models/types';
import {
  AWS_REGION,
  COGNITO_USER_POOL_ID,
  COGNITO_CLIENT_ID,
  COGNITO_IDENTITY_POOL_ID,
  SESSION_STORAGE_KEYS,
} from '../models/constants';

export interface IAuthService {
  login(email: string, password: string): Promise<Session>;
  logout(): void;
  refreshSession(): Promise<Session>;
  isSessionValid(): boolean;
  getSession(): Session | null;
  getAwsCredentials(idToken: string): Promise<AwsCredentials>;
  completeNewPassword(newPassword: string): Promise<Session>;
}

const cognitoProviderClient = new CognitoIdentityProviderClient({
  region: AWS_REGION,
});

const cognitoIdentityClient = new CognitoIdentityClient({
  region: AWS_REGION,
});

function storeSession(session: Session): void {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.idToken, session.idToken);
  sessionStorage.setItem(SESSION_STORAGE_KEYS.accessToken, session.accessToken);
  sessionStorage.setItem(SESSION_STORAGE_KEYS.refreshToken, session.refreshToken);
  sessionStorage.setItem(SESSION_STORAGE_KEYS.tokenExpiry, String(session.expiresAt));
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.idToken);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.accessToken);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.refreshToken);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.tokenExpiry);
}

function parseAuthResult(result: InitiateAuthCommandOutput): Session {
  const authResult = result.AuthenticationResult;
  if (!authResult || !authResult.IdToken || !authResult.AccessToken) {
    throw new Error('Authentication service unavailable');
  }

  const expiresIn = authResult.ExpiresIn ?? 3600;
  const session: Session = {
    idToken: authResult.IdToken,
    accessToken: authResult.AccessToken,
    refreshToken: authResult.RefreshToken ?? '',
    expiresAt: Date.now() + expiresIn * 1000,
  };

  return session;
}

export const authService: IAuthService = {
  async login(email: string, password: string): Promise<Session> {
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      const result = await cognitoProviderClient.send(command);

      // Handle NEW_PASSWORD_REQUIRED challenge
      if (result.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        // Store the challenge session so we can respond later
        sessionStorage.setItem('jaw_challenge_session', result.Session ?? '');
        sessionStorage.setItem('jaw_challenge_username', email);
        throw new Error('NEW_PASSWORD_REQUIRED');
      }

      const session = parseAuthResult(result);
      storeSession(session);
      return session;
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message === 'NEW_PASSWORD_REQUIRED') {
          throw error; // Re-throw so the UI can handle it
        }
        const errorName = (error as { name?: string }).name ?? '';
        if (
          errorName === 'NotAuthorizedException' ||
          errorName === 'UserNotFoundException'
        ) {
          throw new Error('Invalid email or password');
        }
      }
      throw new Error('Authentication service unavailable');
    }
  },

  async completeNewPassword(newPassword: string): Promise<Session> {
    const challengeSession = sessionStorage.getItem('jaw_challenge_session') ?? '';
    const username = sessionStorage.getItem('jaw_challenge_username') ?? '';

    if (!challengeSession || !username) {
      throw new Error('No pending password challenge');
    }

    try {
      const { RespondToAuthChallengeCommand } = await import('@aws-sdk/client-cognito-identity-provider');
      const command = new RespondToAuthChallengeCommand({
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: COGNITO_CLIENT_ID,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
        },
        Session: challengeSession,
      });

      const result = await cognitoProviderClient.send(command);
      const session = parseAuthResult(result);
      storeSession(session);

      // Clean up challenge state
      sessionStorage.removeItem('jaw_challenge_session');
      sessionStorage.removeItem('jaw_challenge_username');

      return session;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorName = (error as { name?: string }).name ?? '';
        if (errorName === 'InvalidPasswordException') {
          throw new Error('Password does not meet requirements (8+ chars, uppercase, lowercase, number)');
        }
      }
      throw new Error('Failed to set new password');
    }
  },

  logout(): void {
    clearSession();
  },

  async refreshSession(): Promise<Session> {
    const currentRefreshToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.refreshToken);
    if (!currentRefreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: COGNITO_CLIENT_ID,
        AuthParameters: {
          REFRESH_TOKEN: currentRefreshToken,
        },
      });

      const result = await cognitoProviderClient.send(command);
      const authResult = result.AuthenticationResult;
      if (!authResult || !authResult.IdToken || !authResult.AccessToken) {
        throw new Error('Authentication service unavailable');
      }

      const expiresIn = authResult.ExpiresIn ?? 3600;
      const session: Session = {
        idToken: authResult.IdToken,
        accessToken: authResult.AccessToken,
        // Refresh token is not returned on refresh flow; keep the existing one
        refreshToken: currentRefreshToken,
        expiresAt: Date.now() + expiresIn * 1000,
      };

      storeSession(session);
      return session;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const errorName = (error as { name?: string }).name ?? '';
        if (
          errorName === 'NotAuthorizedException' ||
          errorName === 'UserNotFoundException'
        ) {
          throw new Error('Invalid email or password');
        }
      }
      throw new Error('Authentication service unavailable');
    }
  },

  isSessionValid(): boolean {
    const expiry = sessionStorage.getItem(SESSION_STORAGE_KEYS.tokenExpiry);
    if (!expiry) return false;
    const expiresAt = Number(expiry);
    if (isNaN(expiresAt)) return false;
    return Date.now() < expiresAt;
  },

  getSession(): Session | null {
    const idToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.idToken);
    const accessToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.accessToken);
    const refreshToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.refreshToken);
    const expiry = sessionStorage.getItem(SESSION_STORAGE_KEYS.tokenExpiry);

    if (!idToken || !accessToken || !refreshToken || !expiry) {
      return null;
    }

    const expiresAt = Number(expiry);
    if (isNaN(expiresAt)) return null;

    return { idToken, accessToken, refreshToken, expiresAt };
  },

  async getAwsCredentials(idToken: string): Promise<AwsCredentials> {
    try {
      const loginKey = `cognito-idp.${AWS_REGION}.amazonaws.com/${COGNITO_USER_POOL_ID}`;

      const getIdCommand = new GetIdCommand({
        IdentityPoolId: COGNITO_IDENTITY_POOL_ID,
        Logins: {
          [loginKey]: idToken,
        },
      });

      const idResult = await cognitoIdentityClient.send(getIdCommand);
      if (!idResult.IdentityId) {
        throw new Error('Authentication service unavailable');
      }

      const getCredentialsCommand = new GetCredentialsForIdentityCommand({
        IdentityId: idResult.IdentityId,
        Logins: {
          [loginKey]: idToken,
        },
      });

      const credentialsResult = await cognitoIdentityClient.send(getCredentialsCommand);
      const creds = credentialsResult.Credentials;
      if (!creds || !creds.AccessKeyId || !creds.SecretKey || !creds.SessionToken) {
        throw new Error('Authentication service unavailable');
      }

      return {
        accessKeyId: creds.AccessKeyId,
        secretAccessKey: creds.SecretKey,
        sessionToken: creds.SessionToken,
        expiration: creds.Expiration ?? new Date(Date.now() + 3600 * 1000),
      };
    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'Authentication service unavailable') {
        throw error;
      }
      throw new Error('Authentication service unavailable');
    }
  },
};
